import React, { useState, useEffect, useMemo } from 'react';
import KanbanBoard from './KanbanBoard';
import { supabase, SUPABASE_CLIENT_FILTER_SELECT, supabase_get_vacancies } from '../services/supabaseService';
import { DEFAULT_KANBAN_STAGES } from '../constants';
import { Briefcase, ChevronDown, X, Search, Loader2, Plus, Building2, CheckCircle2 } from 'lucide-react';
import SearchableCombobox from './SearchableCombobox';
import GlobalVacancyLifecycleFilter from './GlobalVacancyLifecycleFilter';
import { loadUiState, saveUiState, uiStateKey } from '../services/uiState';
import { searchTextIncludes } from '../utils/searchText';
import { isClientUuid, vacancyLinkedToPortalOpdrachtgeverSet, filterPortalVisibleVacancies } from '../utils/clientPortalGuards';
import {
    buildEindklantFilterOptions,
    buildOpdrachtgeverFilterOptions,
    clientFilterOptionSearchHaystack,
    formatClientFilterOptionLabel,
    kanbanVacancyIdFromLifecycleStages,
    parseLegacyVacancyFilterToLifecycleState,
    seedClientMapFromVacancyEmbeds,
    type VacancyLifecycleStage,
    vacancyMatchesLifecycleStages,
    vacancyOpdrachtgeverId,
    vacancyEindklantId,
} from '../utils/vacancyClientFilters';

const Avatar = ({ name, photo }: { name: string; photo?: string }) => (
  <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-slate-100 to-slate-200 flex items-center justify-center font-bold text-[11px] text-slate-500 border border-slate-200 shrink-0 overflow-hidden">
    {photo ? <img src={photo} className="w-full h-full object-cover" alt="" /> : name?.substring(0, 2).toUpperCase()}
  </div>
);

interface GlobalPipelineProps {
  onCandidateSelect?: (id: string) => void;
  vacancies?: any[];
  isLoading?: boolean;
  user?: any;
  portalAllowedClientIds?: string[];
}

const GlobalPipeline: React.FC<GlobalPipelineProps> = ({ onCandidateSelect, vacancies: globalVacancies, isLoading = false, user, portalAllowedClientIds }) => {
  const isClientPortal = typeof window !== 'undefined' && (window.location.hostname.toLowerCase().includes('clientportal') || new URLSearchParams(window.location.search).get('clientportal') === 'true');
  const [vacancies, setVacancies] = useState<any[]>(globalVacancies || []);
  const [portalVacanciesLoading, setPortalVacanciesLoading] = useState(false);
  const defaultLifecycleStages: VacancyLifecycleStage[] = isClientPortal
    ? ['open', 'doing', 'gesloten']
    : ['open'];
  const [lifecycleStages, setLifecycleStages] =
    useState<VacancyLifecycleStage[]>(defaultLifecycleStages);
  const [selectedVacancyId, setSelectedVacancyId] = useState('');
  const [kanbanRefresh, setKanbanRefresh] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  
  const [filterOpdrachtgever, setFilterOpdrachtgever] = useState('');
  const [filterEindklant, setFilterEindklant] = useState('');
  const [filterStatuses, setFilterStatuses] = useState<string[]>([]);
  const [opdrachtgeverOpen, setOpdrachtgeverOpen] = useState(false);
  const [eindklantOpen, setEindklantOpen] = useState(false);
  const [statusOpen, setStatusOpen] = useState(false);
  const [opdrachtgeverSearch, setOpdrachtgeverSearch] = useState('');
  const [eindklantSearch, setEindklantSearch] = useState('');
  const uniqueStatuses = React.useMemo(() => DEFAULT_KANBAN_STAGES.map(s => s.name).sort(), []);

  // Persist filters across page switches
  const uiKey = useMemo(() => uiStateKey('globalPipeline:filters'), []);
  useEffect(() => {
    const saved = loadUiState(uiKey, null as any);
    if (!saved || typeof saved !== 'object') return;
    if (Array.isArray(saved.lifecycleStages) && saved.lifecycleStages.length > 0) {
      setLifecycleStages(
        saved.lifecycleStages.filter((s: string) =>
          ['open', 'doing', 'gesloten'].includes(s)
        ) as VacancyLifecycleStage[]
      );
    } else if (typeof saved.vacancyFilter === 'string') {
      const migrated = parseLegacyVacancyFilterToLifecycleState(
        saved.vacancyFilter,
        isClientPortal
      );
      setLifecycleStages(migrated.stages);
      setSelectedVacancyId(migrated.selectedVacancyId);
    }
    if (typeof saved.selectedVacancyId === 'string') setSelectedVacancyId(saved.selectedVacancyId);
    if (typeof saved.searchQuery === 'string') setSearchQuery(saved.searchQuery);
    if (typeof saved.filterOpdrachtgever === 'string') setFilterOpdrachtgever(saved.filterOpdrachtgever);
    if (typeof saved.filterEindklant === 'string') setFilterEindklant(saved.filterEindklant);
    if (Array.isArray(saved.filterStatuses)) setFilterStatuses(saved.filterStatuses.map(String));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    saveUiState(uiKey, {
      lifecycleStages,
      selectedVacancyId,
      searchQuery,
      filterOpdrachtgever,
      filterEindklant,
      filterStatuses,
    });
  }, [uiKey, lifecycleStages, selectedVacancyId, searchQuery, filterOpdrachtgever, filterEindklant, filterStatuses]);

  const kanbanVacancyId = useMemo(() => {
    if (selectedVacancyId) return selectedVacancyId;
    return kanbanVacancyIdFromLifecycleStages(lifecycleStages);
  }, [selectedVacancyId, lifecycleStages]);

  const [clientMap, setClientMap] = useState<
    Record<string, { name: string; bullhorn_id?: string | number | null; invoice_kvk?: string; data?: unknown }>
  >({});

  useEffect(() => {
    if (!vacancies.length) return;
    setClientMap((prev) => seedClientMapFromVacancyEmbeds(vacancies, prev));

    const ids = new Set<string>();
    vacancies.forEach((v: any) => {
      const og = vacancyOpdrachtgeverId(v);
      const ek = vacancyEindklantId(v);
      if (isClientUuid(og)) ids.add(og);
      if (isClientUuid(ek)) ids.add(ek);
    });
    const arr = Array.from(ids);
    if (arr.length === 0) return;
    let cancelled = false;
    (async () => {
      const merged = seedClientMapFromVacancyEmbeds(vacancies, {});
      const chunks: string[][] = [];
      for (let i = 0; i < arr.length; i += 100) chunks.push(arr.slice(i, i + 100));
      await Promise.all(
        chunks.map(async (chunk) => {
          let { data, error } = await supabase.from('clients').select(SUPABASE_CLIENT_FILTER_SELECT).in('id', chunk);
          if (error) {
            const fallback = await supabase
              .from('clients')
              .select('id, name, logo_url, invoice_kvk, data')
              .in('id', chunk);
            data = fallback.data;
          }
          data?.forEach((c: any) => {
            merged[c.id] = {
              name: c.name,
              bullhorn_id: c.bullhorn_id ?? null,
              invoice_kvk: c.invoice_kvk,
              data: c.data,
            };
          });
        }),
      );
      if (!cancelled) setClientMap((prev) => ({ ...prev, ...merged }));
    })();
    return () => { cancelled = true; };
  }, [vacancies]);

  const vacanciesInLifecycleScope = useMemo(
    () =>
      vacancies.filter((v) =>
        vacancyMatchesLifecycleStages(v, lifecycleStages, { excludePortalArchived: isClientPortal })
      ),
    [vacancies, lifecycleStages, isClientPortal]
  );

  const uniqueOpdrachtgevers = useMemo(
    () => buildOpdrachtgeverFilterOptions(vacanciesInLifecycleScope, clientMap),
    [vacanciesInLifecycleScope, clientMap]
  );

  const uniqueEindklants = useMemo(
    () => buildEindklantFilterOptions(vacanciesInLifecycleScope, clientMap),
    [vacanciesInLifecycleScope, clientMap]
  );

  const filteredPipelineOpdr = uniqueOpdrachtgevers.filter(
    (o) => !opdrachtgeverSearch || searchTextIncludes(clientFilterOptionSearchHaystack(o), opdrachtgeverSearch)
  );
  const filteredPipelineEk = uniqueEindklants.filter(
    (o) => !eindklantSearch || searchTextIncludes(clientFilterOptionSearchHaystack(o), eindklantSearch)
  );
  const selectedPipelineOg = filterOpdrachtgever ? uniqueOpdrachtgevers.find(x => x.id === filterOpdrachtgever) : undefined;
  const selectedPipelineEk = filterEindklant ? uniqueEindklants.find(x => x.id === filterEindklant) : undefined;
  
  const [poolCandidates, setPoolCandidates] = useState<any[]>([]);
  const [candidatesSearch, setCandidatesSearch] = useState('');
  const [isSearchingBackend, setIsSearchingBackend] = useState(false);
  const searchTimeout = React.useRef<NodeJS.Timeout | null>(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedPoolIds, setSelectedPoolIds] = useState<string[]>([]);
  const [targetAddVacancyId, setTargetAddVacancyId] = useState<string>('');
  const [targetAddStageId, setTargetAddStageId] = useState<string>(DEFAULT_KANBAN_STAGES[0].id);

  useEffect(() => {
    // Mirror GlobalShortlist: defer until App finished loading so liveVacancies / user / portalAllowedClientIds are stable.
    if (isLoading) return;
    const fetchVacancies = async () => {
        if (globalVacancies && globalVacancies.length > 0) {
          const deduped = Array.from(new Map(globalVacancies.map((v: any) => [v.id, v])).values());
          let uniqueVacs = deduped;
          if (isClientPortal) {
            const ids = portalAllowedClientIds?.filter((x) => isClientUuid(x));
            if (ids?.length) {
              uniqueVacs = filterPortalVisibleVacancies(
                deduped.filter((v: any) => vacancyLinkedToPortalOpdrachtgeverSet(v, ids)),
              );
              if (uniqueVacs.length === 0 && deduped.length > 0) {
                uniqueVacs = filterPortalVisibleVacancies(deduped);
              }
            }
          }
          setVacancies(uniqueVacs);
          return;
        }
        if (isClientPortal && isClientUuid(user?.clientId)) {
          setPortalVacanciesLoading(true);
          try {
            const data = await supabase_get_vacancies(user.clientId, undefined, true);
            if (data?.length) {
              setVacancies(Array.from(new Map(data.map((v: any) => [v.id, v])).values()));
              return;
            }
            setVacancies([]);
            return;
          } catch (e) {
            console.warn('[GlobalPipeline] portal supabase_get_vacancies failed', e);
            setVacancies([]);
            return;
          } finally {
            setPortalVacanciesLoading(false);
          }
        }
        if (isClientPortal) {
          setVacancies([]);
          return;
        }
        const { data, error } = await supabase.from('vacancies').select('id, title, status, vacancy_number, created_at, client_id, opdrachtgever_id, eindklant_id, clients!vacancies_client_id_fkey(name), data->lifecycleStatus, data->location, data->city, data->werkplek, data->clientName, data->opdrachtgever, data->opdrachtgever_id, data->eindklant, data->eindklant_id, data->isClosed, data->publishedToSite')
          .order('created_at', { ascending: false }).limit(10000);
        if (!error && data) {
          let formatted = data.map((v: any) => {
            // Reconstruct a mock 'data' object so existing code works without changes
            v.data = {
                lifecycleStatus: v.lifecycleStatus,
                location: v.location,
                city: v.city,
                werkplek: v.werkplek,
                clientName: v.clientName,
                opdrachtgever: v.opdrachtgever,
                opdrachtgever_id: v.opdrachtgever_id,
                eindklant: v.eindklant,
                eindklant_id: v.eindklant_id,
                isClosed: v.isClosed,
                publishedToSite: v.publishedToSite
            };
            let status = v.data.lifecycleStatus || v.status || 'Concept';
            const legacyStatus = status.toLowerCase();
            if (['draft', 'concept'].includes(legacyStatus)) status = 'Concept';
            else if (['afgerond', 'complete', 'succesvol', 'plaatsing', 'gesloten'].includes(legacyStatus)) status = 'Gesloten';
            else if (['archief', 'archive', 'done', 'afwezen', 'ingetrokken'].includes(legacyStatus) || v.data.isClosed === true) status = 'Archief';
            else status = 'Open';

            return { ...v, status };
         });

         const uniqueVacs = Array.from(new Map(formatted.map((v: any) => [v.id, v])).values());
         setVacancies(uniqueVacs);
        }
    };
    void fetchVacancies();
  }, [globalVacancies, isLoading, isClientPortal, user?.clientId, portalAllowedClientIds]);

  const handleAddFromPool = async () => {
    if (selectedPoolIds.length === 0 || !targetAddVacancyId) return;
    try {
      const toInsert = selectedPoolIds.map(cid => ({
        vacancy_id: targetAddVacancyId,
        candidate_id: cid,
        status: targetAddStageId
      }));
      const { error } = await supabase.from('applications').insert(toInsert);
      if (error) throw error;
      // Refresh Kanban board
      // Assuming vacancyFilter reflects the selected vacancy
      // Force re-render by updating vacancyFilter state
      setKanbanRefresh((n) => n + 1);
      setShowAddModal(false);
      setSelectedPoolIds([]);
    } catch (e) { console.error(e); }
  };

  const hasVacancyCatalog =
    vacancies.length > 0 || (globalVacancies != null && globalVacancies.length > 0);
  const pipelineSyncBlocking =
    portalVacanciesLoading || (isLoading && !hasVacancyCatalog);

  return (
    <div className="w-full h-full flex flex-col bg-white animate-fadeIn">
       <div className="px-5 py-4 border-b border-slate-200 bg-white shrink-0">
         <div className="flex items-center justify-between mb-3">
           <div className="flex items-center gap-3">
             <div className="w-8 h-8 rounded-xl bg-red-50 flex items-center justify-center">
               <Briefcase size={15} className="text-red-500" />
             </div>
             <div>
               <h1 className="text-[15px] font-bold text-slate-900">Pipeline</h1>
               <p className="text-[11px] text-slate-400 font-medium">Overzicht van alle wervingspijplijnen</p>
             </div>
           </div>
           <div className="flex items-center gap-2">
             {(searchQuery || filterOpdrachtgever || filterEindklant || filterStatuses.length > 0) && (
               <button onClick={() => { setSearchQuery(''); setFilterOpdrachtgever(''); setFilterEindklant(''); setFilterStatuses([]); }} className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-[11px] font-medium text-slate-500 hover:text-slate-700 bg-slate-100 hover:bg-slate-200 transition-colors">
                 <X size={11} /> Wissen
               </button>
             )}
           </div>
         </div>
         
         <div className={`w-full grid grid-cols-1 sm:grid-cols-2 ${isClientPortal ? 'xl:grid-cols-4' : 'xl:grid-cols-5'} gap-2 relative z-50 items-stretch`}>
           <GlobalVacancyLifecycleFilter
             vacancies={globalVacancies && globalVacancies.length > 0 ? globalVacancies : vacancies}
             lifecycleStages={lifecycleStages}
             onLifecycleStagesChange={setLifecycleStages}
             selectedVacancyId={selectedVacancyId}
             onSelectedVacancyIdChange={setSelectedVacancyId}
             clientScope={{
               opdrachtgeverId: filterOpdrachtgever,
               eindklantId: filterEindklant,
             }}
             clientMap={clientMap}
             isClientPortal={isClientPortal}
           />

         {/* Search Box */}
         <div className="relative min-w-0 w-full z-40">
           <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
           <input type="text" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} placeholder="Zoek kandidaat of vacature..." className="w-full pl-8 pr-3 py-1.5 text-[12px] bg-slate-50 border border-slate-200 rounded-lg outline-none focus:border-indigo-400 focus:bg-white transition-colors font-medium" />
         </div>

         {/* Opdrachtgever — verborgen in het client portal: de ingelogde organisatie ÍS de opdrachtgever. */}
         {!isClientPortal && (
         <div className="relative z-40 min-w-0 w-full">
           <button onClick={() => { setOpdrachtgeverOpen(!opdrachtgeverOpen); setEindklantOpen(false); setStatusOpen(false); }} className={`w-full flex items-center justify-between gap-2 px-3 py-1.5 rounded-lg border text-[12px] font-medium transition-all ${filterOpdrachtgever ? 'bg-emerald-50 border-emerald-200 text-emerald-700' : 'bg-white border-slate-200 text-slate-600 hover:border-slate-300'}`}>
             <Building2 size={11} className="shrink-0" />
             <span className="min-w-0 flex-1 text-left truncate">{selectedPipelineOg ? formatClientFilterOptionLabel(selectedPipelineOg) : 'Opdrachtgever'}</span>
             {filterOpdrachtgever ? <X size={10} onClick={e => { e.stopPropagation(); setFilterOpdrachtgever(''); }} className="text-emerald-400 shrink-0" /> : <ChevronDown size={10} className="shrink-0" />}
           </button>
           {opdrachtgeverOpen && (
             <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-slate-200 rounded-xl shadow-xl z-50">
               <div className="p-2 border-b border-slate-100"><input autoFocus value={opdrachtgeverSearch} onChange={e => setOpdrachtgeverSearch(e.target.value)} placeholder="Zoeken..." className="w-full px-2.5 py-1.5 text-[11px] border border-slate-200 rounded-lg outline-none" /></div>
               <div className="max-h-48 overflow-y-auto">
                 <button onClick={() => { setFilterOpdrachtgever(''); setOpdrachtgeverOpen(false); setOpdrachtgeverSearch(''); }} className="w-full text-left px-3 py-2 text-[11px] font-medium text-slate-500 hover:bg-slate-50">Alle opdrachtgevers</button>
                 {filteredPipelineOpdr.map(o => (
                     <button
                       key={o.id}
                       onClick={() => { setFilterOpdrachtgever(o.id); setOpdrachtgeverOpen(false); setOpdrachtgeverSearch(''); }}
                       className={`w-full text-left px-3 py-2 text-[11px] hover:bg-emerald-50 transition-colors ${filterOpdrachtgever === o.id ? 'bg-emerald-50 text-emerald-700 font-semibold' : 'text-slate-700'}`}
                     >
                       <div className="min-w-0 font-medium truncate">
                         {o.companyNumber ? (
                           <span className="font-mono text-[10px] text-slate-500 mr-1.5">{o.companyNumber}</span>
                         ) : null}
                         {o.companyName}
                       </div>
                     </button>
                 ))}
               </div>
             </div>
           )}
         </div>
         )}

         {/* Eindklant */}
         <div className="relative z-40 min-w-0 w-full">
           <button onClick={() => { setEindklantOpen(!eindklantOpen); setOpdrachtgeverOpen(false); setStatusOpen(false); }} className={`w-full flex items-center justify-between gap-2 px-3 py-1.5 rounded-lg border text-[12px] font-medium transition-all ${filterEindklant ? 'bg-indigo-50 border-indigo-200 text-indigo-700' : 'bg-white border-slate-200 text-slate-600 hover:border-slate-300'}`}>
             <Building2 size={11} className="shrink-0" />
             <span className="min-w-0 flex-1 text-left truncate">{selectedPipelineEk ? formatClientFilterOptionLabel(selectedPipelineEk) : 'Werkplek'}</span>
             {filterEindklant ? <X size={10} onClick={e => { e.stopPropagation(); setFilterEindklant(''); }} className="text-indigo-400 shrink-0" /> : <ChevronDown size={10} className="shrink-0" />}
           </button>
           {eindklantOpen && (
            <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-slate-200 rounded-xl shadow-xl z-50">
              <div className="p-2 border-b border-slate-100">
                <input autoFocus value={eindklantSearch} onChange={e => setEindklantSearch(e.target.value)} placeholder="Zoeken..." className="w-full px-2.5 py-1.5 text-[11px] border border-slate-200 rounded-lg outline-none" />
              </div>
              <div className="max-h-56 overflow-y-auto">
                <button onClick={() => { setFilterEindklant(''); setEindklantOpen(false); setEindklantSearch(''); }} className="w-full text-left px-3 py-2 text-[11px] font-medium text-slate-500 hover:bg-slate-50">Alle werkplekken</button>
                {filteredPipelineEk.map(o => (
                    <button
                      key={o.id}
                      onClick={() => { setFilterEindklant(o.id); setEindklantOpen(false); setEindklantSearch(''); }}
                      className={`w-full text-left px-3 py-2 text-[11px] hover:bg-indigo-50 transition-colors ${filterEindklant === o.id ? 'bg-indigo-50 text-indigo-700 font-semibold' : 'text-slate-700'}`}
                    >
                      <div className="min-w-0 font-medium truncate">
                        {o.companyNumber ? (
                          <span className="font-mono text-[10px] text-slate-500 mr-1.5">{o.companyNumber}</span>
                        ) : null}
                        {o.companyName}
                      </div>
                    </button>
                ))}
              </div>
            </div>
           )}
         </div>

         {/* Status (multi-select) */}
         <div className="relative z-40 min-w-0 w-full">
           <button onClick={() => { setStatusOpen(!statusOpen); setOpdrachtgeverOpen(false); setEindklantOpen(false); }} className={`w-full flex items-center justify-between gap-2 px-3 py-1.5 rounded-lg border text-[12px] font-medium transition-all ${filterStatuses.length > 0 ? 'bg-amber-50 border-amber-200 text-amber-700' : 'bg-white border-slate-200 text-slate-600 hover:border-slate-300'}`}>
             <CheckCircle2 size={11} className="shrink-0" />
             <span className="min-w-0 flex-1 text-left truncate">{filterStatuses.length > 0 ? `${filterStatuses.length} status` : 'Status'}</span>
             {filterStatuses.length > 0 ? <X size={10} onClick={e => { e.stopPropagation(); setFilterStatuses([]); }} className="text-amber-400 shrink-0" /> : <ChevronDown size={10} className="shrink-0" />}
           </button>
           {statusOpen && (
             <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-slate-200 rounded-xl shadow-xl z-50">
               <div className="max-h-48 overflow-y-auto">
                 <button onClick={() => { setFilterStatuses([]); }} className="w-full text-left px-3 py-2 text-[11px] text-slate-500 hover:bg-slate-50">Alle statussen</button>
                 {uniqueStatuses.map(s => {
                   const checked = filterStatuses.includes(s);
                   return (
                     <button
                       key={s}
                       onClick={() => {
                         setFilterStatuses(prev => prev.includes(s) ? prev.filter(x => x !== s) : [...prev, s]);
                       }}
                       className={`w-full text-left px-3 py-2 text-[11px] hover:bg-amber-50 transition-colors ${checked ? 'bg-amber-50 text-amber-700 font-semibold' : 'text-slate-700'}`}
                     >
                       <span className="inline-flex items-center gap-2">
                         <input type="checkbox" readOnly checked={checked} className="rounded border-slate-300 text-amber-600" />
                         <span>{s}</span>
                       </span>
                     </button>
                   );
                 })}
               </div>
             </div>
           )}
         </div>
         </div>
       </div>
       
       {(opdrachtgeverOpen || eindklantOpen || statusOpen) && <div className="fixed inset-0 z-30" onClick={() => { setOpdrachtgeverOpen(false); setEindklantOpen(false); setStatusOpen(false); }} />}
       
        <div className="flex-1 overflow-hidden flex flex-col bg-slate-50 relative">
          {pipelineSyncBlocking && (
             <div className="absolute inset-0 z-50 flex items-center justify-center bg-white/80 backdrop-blur-sm rounded-[2rem]">
                 <div className="flex flex-col items-center gap-4">
                     <Loader2 size={32} className="text-[#e51b23] animate-spin" />
                     <p className="text-slate-500 font-bold text-sm uppercase tracking-widest animate-pulse">Pipelines synchroniseren...</p>
                 </div>
             </div>
          )}
          <KanbanBoard 
            key={`${kanbanVacancyId}-${kanbanRefresh}`}
            vacancyId={kanbanVacancyId}
            lifecycleStages={lifecycleStages}
            selectedVacancyId={selectedVacancyId}
            onCandidateSelect={onCandidateSelect} 
            onCustomAddCandidateClick={() => setShowAddModal(true)} 
            globalVacancies={vacancies}
            user={user}
            filterOpdrachtgever={filterOpdrachtgever}
            filterEindklant={filterEindklant}
            filterStatus={filterStatuses}
            searchQuery={searchQuery}
          />
        </div>
        
      {/* Add from Talent Pool Modal */}
      {showAddModal && (
        <div className="fixed inset-0 z-[200] flex justify-end bg-slate-900/60 backdrop-blur-sm transition-opacity animate-in fade-in">
          <div className="bg-white w-full max-w-[550px] shadow-2xl h-full flex flex-col animate-slideInRight duration-300">
            {/* Header */}
            <div className="p-5 sm:px-6 sm:py-5 border-b border-slate-100 flex items-center justify-between bg-white shrink-0">
              <h3 className="text-xl font-black text-slate-900 uppercase tracking-tight">Kandidaat Toevoegen</h3>
              <button onClick={() => setShowAddModal(false)} className="p-1.5 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors">
                <X size={20} />
              </button>
            </div>

            {/* Scrollable Body */}
            <div className="flex-1 overflow-y-auto custom-scrollbar p-5 sm:p-6 bg-[#f8fafc] space-y-5">
              
              {/* Box 1: Select Vacancy */}
              <div className="bg-white p-5 rounded-xl border border-slate-100 shadow-sm">
                <div className="flex justify-between items-center mb-3">
                  <label className="text-[10px] font-black text-red-600 uppercase tracking-widest">1. Kies Vacature</label>
                </div>
                <SearchableCombobox
                  options={vacancies.map(v => ({
                      id: v.id,
                      title: v.title,
                      subtitle: v.data?.location || 'Geen locatie',
                      badge: v.vacancy_number || undefined
                  }))}
                  value={targetAddVacancyId}
                  onChange={(val) => setTargetAddVacancyId(val)}
                  placeholder="Zoek en selecteer vacature..."
                  searchPlaceholder="Zoek op vacaturenaam, nummer of werkplek..."
                  icon={Briefcase}
                />
              </div>

              {/* Box 2: Select Candidates */}
              <div className="bg-white p-5 rounded-xl border border-slate-100 shadow-sm flex flex-col min-h-[300px]">
                <label className="text-[10px] font-black text-red-600 uppercase tracking-widest mb-3">2. Kies Kandidaten (Talentpool)</label>
                <div className="relative mb-4">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
                  <input
                    type="text"
                    placeholder="Zoek candidaten op naam, rol etc..."
                    value={candidatesSearch}
                    onChange={(e) => {
                      const val = e.target.value;
                      setCandidatesSearch(val);
                      if (searchTimeout.current) clearTimeout(searchTimeout.current);
                      if (val.trim().length >= 2) {
                        setIsSearchingBackend(true);
                        searchTimeout.current = setTimeout(async () => {
                          try {
                            const { supabase_get_paginated_talent_pool } = await import('../services/supabaseService');
                            const res = await supabase_get_paginated_talent_pool(undefined, 1, 50, val);
                            if (res && res.data) {
                              const extracted = res.data.map((tp: any) => tp.candidate).filter(Boolean);
                              const unique = Array.from(new Map(extracted.map((c: any) => [c.id, c])).values());
                              setPoolCandidates(unique);
                            } else {
                              setPoolCandidates([]);
                            }
                          } catch(e) {} finally { setIsSearchingBackend(false); }
                        }, 400);
                      } else {
                        setPoolCandidates([]);
                      }
                    }}
                    className="w-full text-xs font-bold bg-white border border-slate-200 rounded-lg focus:ring-2 focus:ring-[#0073e6]/20 focus:border-[#0073e6] outline-none pl-9 pr-4 py-3 placeholder:font-medium placeholder:text-slate-400 transition-colors"
                  />
                  {isSearchingBackend && <Loader2 size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 animate-spin" />}
                </div>

                <div className="flex-1 bg-slate-50 border border-slate-100 rounded-xl overflow-hidden relative flex flex-col">
                  {candidatesSearch.trim().length < 2 && poolCandidates.length === 0 ? (
                    <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                      <span className="text-[11px] font-medium text-slate-400">Typ minimaal 2 letters om te zoeken in de talentpool...</span>
                    </div>
                  ) : poolCandidates.length === 0 && !isSearchingBackend ? (
                    <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none text-[11px] text-slate-400">
                      Geen kandidaten gevonden.
                    </div>
                  ) : (
                    <div className="flex-1 overflow-y-auto max-h-64 p-2 space-y-1">
                      {poolCandidates.map(c => (
                        <label key={c.id} className="flex items-center gap-3 p-3 hover:bg-white rounded-lg cursor-pointer transition-colors border border-transparent hover:border-slate-100 hover:shadow-sm">
                          <input
                            type="checkbox"
                            checked={selectedPoolIds.includes(c.id)}
                            onChange={(e) => {
                              if (e.target.checked) setSelectedPoolIds(prev => [...prev, c.id]);
                              else setSelectedPoolIds(prev => prev.filter(id => id !== c.id));
                            }}
                            className="rounded border-slate-300 text-indigo-600 focus:ring-indigo-600 ml-1"
                          />
                          <Avatar name={c.name} photo={c.photo_url || c.avatar_url} />
                          <div className="flex flex-col flex-1 min-w-0">
                            <span className="text-sm font-bold text-slate-800 truncate">{c.name}</span>
                            <div className="flex items-center gap-2 mt-0.5 min-w-0">
                                {c.candidate_number && (
                                    <span className="text-[10px] font-black tracking-widest bg-slate-100 text-slate-500 px-1.5 py-0.5 rounded border border-slate-200 shrink-0">
                                        #{c.candidate_number}
                                    </span>
                                )}
                                <span className="text-xs font-medium text-slate-500 truncate">{c.job_title || c.role || c.email}</span>
                            </div>
                          </div>
                        </label>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Box 3: Select Status */}
              <div className="bg-white p-5 rounded-xl border border-slate-100 shadow-sm">
                <label className="text-[10px] font-black text-red-600 uppercase tracking-widest mb-3 block">3. Kies Pipeline Status</label>
                <div className="relative">
                  <select
                    value={targetAddStageId}
                    onChange={(e) => setTargetAddStageId(e.target.value)}
                    className="w-full text-xs font-bold text-slate-700 bg-white border border-slate-200 rounded-lg focus:ring-2 focus:ring-[#0073e6]/20 focus:border-[#0073e6] outline-none appearance-none px-4 py-3 transition-colors"
                  >
                    {DEFAULT_KANBAN_STAGES.map(s => (
                      <option key={s.id} value={s.id}>{s.name}</option>
                    ))}
                  </select>
                  <ChevronDown className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" size={16} />
                </div>
              </div>

            </div>

            {/* Footer */}
            <div className="p-4 sm:px-6 border-t border-slate-100 bg-white shrink-0">
              <button
                onClick={handleAddFromPool}
                disabled={!targetAddVacancyId || selectedPoolIds.length === 0}
                className="w-full px-4 py-3.5 text-[11px] font-black tracking-widest uppercase text-white bg-[#4f39f6] hover:bg-[#3f2bd2] disabled:opacity-50 disabled:hover:bg-[#4f39f6] disabled:cursor-not-allowed rounded-xl transition-all shadow-md shadow-indigo-500/20"
              >
                + {selectedPoolIds.length} Kandidaten Toevoegen
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GlobalPipeline;
